import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosRequestConfig, AxiosError } from 'axios';
import axios from 'axios';

/**
 * Pure transport. It issues the request and normalises the failure shape —
 * nothing more.
 *
 * It deliberately does *not* interpret auth failures. Deciding whether a 403
 * means "your session is gone" or "you may not do this" needs the token, which
 * only the data-access layer owns; see `makeBaseQuery` in
 * `libs/data-access/src/lib/core/BaseApi.ts`.
 */
export const axiosBaseQuery =
  (
    baseURL: string,
    token?: string
  ): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
    },
    unknown,
    unknown
  > =>
  async (arg) => {
    if (!arg) {
      return {
        error: {
          status: 400,
          data: 'Request arguments are undefined',
        },
      };
    }
    const { url, method = 'GET', data, params } = arg;
    try {
      // Check localStorage for SageMaker setting
      const enableSageMaker = localStorage.getItem('enableSageMaker');

      const axiosInstance = axios.create({
        baseURL,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(enableSageMaker ? { 'X-Enable-SageMaker': 'true' } : {}),
        },
      });
      const finalUrl = url.endsWith('/') ? url : `${url}/`;

      const result = await axiosInstance({
        url: finalUrl,
        method,
        data,
        params,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;

      return {
        error: {
          status: err.response?.status || 500,
          data: err.response?.data || err.message,
        },
      };
    }
  };
